import { downsampleAudioSamples, encodeWav, TARGET_SAMPLE_RATE } from './recordWavChunk'

type BrowserAudioContext = typeof AudioContext

declare global {
  interface Window {
    webkitAudioContext?: BrowserAudioContext
  }
}

export type VoiceActivityParty = 'agent' | 'customer'

export interface VoiceActivitySegment {
  id: string
  party: VoiceActivityParty
  startedAt: number
  endedAt: number
  wavBlob: Blob
}

export interface VoiceActivityRecorder {
  start: () => Promise<void>
  stop: () => Promise<void>
}

interface VoiceActivityRecorderOptions {
  party: VoiceActivityParty
  stream: MediaStream
  onSpeechStart: (payload: { id: string; party: VoiceActivityParty; startedAt: number }) => void
  onSpeechDiscarded: (payload: { id: string }) => void
  onSegment: (segment: VoiceActivitySegment) => void
  onError: (error: Error) => void
}

const RMS_THRESHOLD = 0.012
const SPEECH_START_MS = 200
const SILENCE_END_MS = 1500
const MIN_SPEECH_MS = 500
const MAX_CHUNK_MS = 10000

const mergeSamples = (chunks: Float32Array[]) => {
  const sampleCount = chunks.reduce((total, chunk) => total + chunk.length, 0)
  const merged = new Float32Array(sampleCount)
  let offset = 0

  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }

  return merged
}

const calculateRms = (samples: Float32Array) => {
  let sum = 0

  for (const sample of samples) {
    sum += sample * sample
  }

  return Math.sqrt(sum / samples.length)
}

const createSegmentId = (party: VoiceActivityParty, startedAt: number) =>
  `${party}-${startedAt}-${crypto.randomUUID()}`

export const createVoiceActivityRecorder = ({
  party,
  stream,
  onSpeechStart,
  onSpeechDiscarded,
  onSegment,
  onError,
}: VoiceActivityRecorderOptions): VoiceActivityRecorder => {
  let audioContext: AudioContext | null = null
  let source: MediaStreamAudioSourceNode | null = null
  let processor: ScriptProcessorNode | null = null
  let silentGain: GainNode | null = null
  let isRunning = false
  let isSpeaking = false
  let speechStartCandidateAt = 0
  let silenceStartedAt = 0
  let currentSegmentId = ''
  let currentStartedAt = 0
  let chunks: Float32Array[] = []
  let candidateChunks: Float32Array[] = []

  const resetSpeech = () => {
    isSpeaking = false
    speechStartCandidateAt = 0
    silenceStartedAt = 0
    currentSegmentId = ''
    currentStartedAt = 0
    chunks = []
    candidateChunks = []
  }

  const finishSegment = (endedAt: number) => {
    if (!currentSegmentId || !currentStartedAt) {
      resetSpeech()
      return
    }

    const durationMs = endedAt - currentStartedAt
    if (durationMs < MIN_SPEECH_MS || chunks.length === 0 || !audioContext) {
      onSpeechDiscarded({ id: currentSegmentId })
      resetSpeech()
      return
    }

    const merged = mergeSamples(chunks)
    const wavBlob = encodeWav(
      downsampleAudioSamples(merged, audioContext.sampleRate),
      TARGET_SAMPLE_RATE,
    )

    onSegment({
      id: currentSegmentId,
      party,
      startedAt: currentStartedAt,
      endedAt,
      wavBlob,
    })

    resetSpeech()
  }

  const start = async () => {
    if (isRunning) return

    const audioTracks = stream.getAudioTracks().filter((track) => track.readyState === 'live')
    if (!audioTracks.length) {
      throw new Error('No hay audio activo para escuchar.')
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      throw new Error('El navegador no soporta AudioContext.')
    }

    audioContext = new AudioContextClass()
    source = audioContext.createMediaStreamSource(new MediaStream(audioTracks))
    processor = audioContext.createScriptProcessor(2048, 1, 1)
    silentGain = audioContext.createGain()
    silentGain.gain.value = 0
    isRunning = true

    processor.onaudioprocess = (event) => {
      if (!isRunning || !audioContext) return

      const now = Date.now()
      const samples = new Float32Array(event.inputBuffer.getChannelData(0))
      const hasVoice = calculateRms(samples) >= RMS_THRESHOLD

      if (hasVoice && !isSpeaking) {
        speechStartCandidateAt ||= now
        candidateChunks.push(samples)

        if (now - speechStartCandidateAt >= SPEECH_START_MS) {
          currentStartedAt = speechStartCandidateAt
          currentSegmentId = createSegmentId(party, currentStartedAt)
          isSpeaking = true
          silenceStartedAt = 0
          chunks = [...candidateChunks]
          candidateChunks = []
          onSpeechStart({ id: currentSegmentId, party, startedAt: currentStartedAt })
        }

        return
      }

      if (!hasVoice && !isSpeaking) {
        speechStartCandidateAt = 0
        candidateChunks = []
        return
      }

      if (isSpeaking) {
        chunks.push(samples)

        if (hasVoice) {
          silenceStartedAt = 0
        } else {
          silenceStartedAt ||= now
        }

        if (
          now - currentStartedAt >= MAX_CHUNK_MS ||
          (silenceStartedAt && now - silenceStartedAt >= SILENCE_END_MS)
        ) {
          finishSegment(now)
        }
      }
    }

    source.connect(processor)
    processor.connect(silentGain)
    silentGain.connect(audioContext.destination)
  }

  const stop = async () => {
    if (!isRunning) return

    isRunning = false

    try {
      if (isSpeaking) {
        finishSegment(Date.now())
      }
    } catch (error) {
      onError(error instanceof Error ? error : new Error('No se pudo cerrar el segmento de voz.'))
    }

    processor?.disconnect()
    source?.disconnect()
    silentGain?.disconnect()

    if (audioContext?.state !== 'closed') {
      await audioContext?.close()
    }

    audioContext = null
    source = null
    processor = null
    silentGain = null
    resetSpeech()
  }

  return {
    start,
    stop,
  }
}
